/**
 * Tutoring math, shared by the treasurer's screens, families' and tutors' views, and the AI:
 * hours left, what a family owes, how long they've been with you (for grandfathered rates),
 * package savings, and what each tutor has earned.
 */
import { overallTotal } from "./overall.ts";

export interface TutoringStudent {
  id: string;
  name: string;
  family: string;
  contact: string;
  /** For group classes: students with the same class name are taught together. */
  className: string;
  since: string;
  rateCashCents: number;
  rateCardCents: number;
  grandfathered: boolean;
  billing: "hourly" | "package" | "monthly";
  /** Prepaid plan: hours each renewal buys, and its price (Zelle/cash and card), in cents. */
  planMinutes?: number;
  planCents?: number;
  planCardCents?: number;
  /** The tutor who usually teaches them (null: you). */
  tutorId?: string | null;
  userId: string | null;
  /** The linked account said "Yes, this is me". */
  linkConfirmed?: boolean;
  note: string;
  archived: boolean;
  /** Looking for a tutor: every tutor sees them (name and class only) and can take them. */
  seeking?: boolean;
  /**
   * The market price: what a tutor is paid per hour for this student, shown to every tutor (in
   * cents; 0 = not listed). Listed, a tutor takes the student or a lesson right away at this pay;
   * not listed, taking is a request that waits for Miles's OK (he sets the pay when he approves).
   */
  marketPayCents?: number;
}

/**
 * What a tutor is paid per hour for one student (Miles, 2026-11-02: "I set the price for each
 * student and teacher pairing"). Each lesson keeps the rate it was taught at (TutoringEntry.payCents).
 */
export interface TutoringPairing {
  id: string;
  studentId: string;
  tutorId: string;
  payCents: number;
}

/**
 * A tutor taking an unclaimed lesson, or a student who is looking for a tutor. With a market price
 * it's approved straight away; otherwise it waits for Miles ("pending") until he approves or
 * declines it. Kept forever as the record of who took what.
 */
export interface TutoringClaim {
  id: string;
  kind: "lesson" | "student";
  slotId: string | null;
  studentId: string;
  tutorId: string;
  status: "pending" | "approved" | "declined" | "withdrawn";
  /** The pay per hour it was taken at (the market price, or what Miles set when approving). */
  payCents: number;
  createdAt: string;
  decidedAt?: string | null;
  decidedBy?: string | null;
}

/**
 * Pay per hour for a pairing Miles hasn't priced yet (Miles, 2026-11-02: "default pay is minimum
 * wage in UCSD/Irvine until I set it higher for each"). San Diego's minimum wage from 2026-01-01 is
 * $17.75 (the state's, which Irvine follows, is $16.90), so this is legal in both places.
 */
export const DEFAULT_TUTOR_PAY_CENTS = 1775;

/** What a tutor is paid per hour for a student: the pairing's own rate, or the default. */
export function pairingPay(
  studentId: string | null,
  tutorId: string | null,
  pairings: readonly TutoringPairing[],
): number {
  if (!tutorId) return 0;
  const p = pairings.find((x) => x.studentId === studentId && x.tutorId === tutorId);
  return p ? p.payCents : DEFAULT_TUTOR_PAY_CENTS;
}

/** Taken right away (a market price is listed) or asked for (waits for Miles). */
export function claimMode(student: Pick<TutoringStudent, "marketPayCents">): "take" | "ask" {
  return (student.marketPayCents ?? 0) > 0 ? "take" : "ask";
}

/** A lesson with a student but no tutor yet, that any tutor can take. */
export function isUnclaimed(slot: TutoringSlot): boolean {
  return Boolean(slot.unclaimed) && !slot.tutorId && !!slot.studentId && slot.status === "booked";
}

/** A student looking for a tutor, that any tutor can take. */
export function isSeeking(student: TutoringStudent): boolean {
  return Boolean(student.seeking) && !student.tutorId && !student.archived;
}

/**
 * Which lessons a tutor sees (Miles, 2026-11-02): their own, every lesson of the students paired
 * with them, and any lesson still waiting for a tutor. Nothing else. Cancelled lessons show only
 * when marked "Didn't happen".
 */
export function tutorSees(
  slot: TutoringSlot,
  tutorId: string,
  students: readonly Pick<TutoringStudent, "id" | "tutorId">[],
): "own" | "paired" | "unclaimed" | null {
  if (slot.status === "cancelled" && slot.outcome !== "missed") return null;
  if (slot.tutorId === tutorId) return "own";
  if (slot.studentId && students.find((s) => s.id === slot.studentId)?.tutorId === tutorId)
    return "paired";
  if (isUnclaimed(slot)) return "unclaimed";
  return null;
}

export interface Tutor {
  id: string;
  name: string;
  contact: string;
  /** What you pay them per hour taught. */
  payCents: number;
  userId: string | null;
  linkConfirmed?: boolean;
  /**
   * Their person in a Splits group: the same person, so their tutoring pay and what they owe in
   * your groups meet in one overall total on People. Pay is not put into the group itself.
   */
  splitGroupId?: string | null;
  splitPersonId?: string | null;
  note: string;
  archived: boolean;
}

export interface TutoringPackage {
  id: string;
  name: string;
  unit: "hours" | "months";
  amount: number;
  priceCashCents: number;
  priceCardCents: number;
  active: boolean;
}

export interface TutoringEntry {
  id: string;
  kind: "session" | "purchase" | "payment" | "item" | "payout" | "adjust";
  studentId: string | null;
  tutorId: string | null;
  packageId: string | null;
  on: string;
  minutes: number;
  cents: number;
  method: "" | "cash" | "zelle" | "card" | "other";
  note: string;
  /** Sessions: the calendar lesson it was logged from. */
  slotId?: string | null;
  /**
   * Sessions: "auto" logged itself when the lesson ended; "hand" someone marked it done.
   * Payouts: "auto" Tutoring put it into a group as a System payment (before 2026-10-23); "hand"
   * you paid them yourself (Zelle, cash).
   */
  logged?: "auto" | "hand";
  /** Payouts marked "not paid" (logged by mistake): kept and shown, never counted. When, if so. */
  voided?: string | null;
  /** And who marked it. */
  voidedBy?: string | null;
  /**
   * Sessions: the tutor's pay per hour for this lesson, kept when it was logged (the pairing's
   * rate then). Absent (older lessons): the tutor's own rate.
   */
  payCents?: number | null;
}

/** A lesson's pay per hour: the rate it was logged with, or (older lessons) the tutor's own. */
export const lessonRate = (e: TutoringEntry, tutor: Tutor): number =>
  e.payCents ?? tutor.payCents ?? 0;

export interface TutoringSlot {
  id: string;
  tutorId: string | null;
  studentId: string | null;
  startsAt: string;
  endsAt: string;
  status: "open" | "booked" | "done" | "cancelled";
  note: string;
  /** "missed": marked not done (status is cancelled, but it still shows as "Didn't happen"). */
  outcome?: "" | "missed";
  /** Waiting for a tutor: any tutor can take it (tutorId is null until one does). */
  unclaimed?: boolean;
  /** Who last set its exact times, when a tutor did ("Jianing Dong"). */
  timesBy?: string;
}

export interface StudentSummary {
  /** Prepaid minutes bought, used and left (packages). */
  boughtMinutes: number;
  usedMinutes: number;
  leftMinutes: number;
  /** Everything charged (packages, hourly sessions, other things sold) and paid, in cents. */
  chargedCents: number;
  paidCents: number;
  /** Positive: they owe you. */
  balanceCents: number;
  lastSession: string | null;
  sessions: number;
}

export function studentSummary(
  studentId: string,
  entries: readonly TutoringEntry[],
): StudentSummary {
  let bought = 0;
  let used = 0;
  let charged = 0;
  let paid = 0;
  let last: string | null = null;
  let sessions = 0;
  for (const e of entries) {
    if (e.studentId !== studentId) continue;
    switch (e.kind) {
      case "purchase":
        bought += e.minutes;
        charged += e.cents;
        break;
      case "session":
        used += e.minutes;
        charged += e.cents; // hourly billing charges per session; package sessions are 0
        sessions++;
        if (!last || e.on > last) last = e.on;
        break;
      case "item":
        charged += e.cents;
        break;
      case "payment":
        paid += e.cents;
        break;
      case "adjust":
        charged += e.cents;
        bought += e.minutes;
        break;
    }
  }
  return {
    boughtMinutes: bought,
    usedMinutes: used,
    leftMinutes: bought - used,
    chargedCents: charged,
    paidCents: paid,
    balanceCents: charged - paid,
    lastSession: last,
    sessions,
  };
}

/** What an hourly session costs at the student's rate (cash or card price). */
export function sessionCharge(student: TutoringStudent, minutes: number, card = false): number {
  const rate = card ? student.rateCardCents || student.rateCashCents : student.rateCashCents;
  return Math.round((rate * minutes) / 60);
}

/** "2 years, 3 months", for "client since". */
export function tenure(since: string, today: string): string {
  const [y1, m1, d1] = since.split("-").map(Number) as [number, number, number];
  const [y2, m2, d2] = today.split("-").map(Number) as [number, number, number];
  let months = (y2 - y1) * 12 + (m2 - m1) - (d2 < d1 ? 1 : 0);
  if (months < 1) return "less than a month";
  const years = Math.floor(months / 12);
  months -= years * 12;
  const part = (n: number, w: string) => (n ? `${n} ${w}${n === 1 ? "" : "s"}` : "");
  return [part(years, "year"), part(months, "month")].filter(Boolean).join(", ");
}

/** How much a package saves compared with paying the hourly cash rate (whole percent). */
export function packageSavings(pkg: TutoringPackage, hourlyCashCents: number): number | null {
  if (pkg.unit !== "hours" || hourlyCashCents <= 0) return null;
  const full = hourlyCashCents * pkg.amount;
  if (full <= 0 || pkg.priceCashCents >= full) return null;
  return Math.round((1 - pkg.priceCashCents / full) * 100);
}

export interface TutorSummary {
  minutes: number;
  /** What they earned teaching: their pay per hour times the hours taught. */
  earnedCents: number;
  /** Money you paid them yourself (Zelle, cash), not counting payouts marked not paid. */
  paidOutCents: number;
  /** Things they bought from you through Tutoring (count against their pay). */
  boughtCents: number;
  /**
   * Their tutoring pay still to settle, shown in their overall total on People: earned less paid
   * out and bought. Positive: you owe them.
   */
  owedCents: number;
  sessions: number;
}

/**
 * Tutoring is its own account (Miles, 2026-10-23): it changes only from lessons and real payments
 * in its own records. A payout counts when you paid it yourself, unless it was removed (logged by
 * mistake). An "auto" payout is the old coupling's side of a System payment Tutoring once put into
 * a group: never real money, so it counts nowhere.
 */
export const payoutCounts = (e: TutoringEntry): boolean => !e.voided && e.logged !== "auto";

export function tutorSummary(tutor: Tutor, entries: readonly TutoringEntry[]): TutorSummary {
  let minutes = 0;
  /** Pay per hour times minutes, summed: each lesson at its own rate, rounded once at the end. */
  let rateMinutes = 0;
  let paidOut = 0;
  let bought = 0;
  let sessions = 0;
  for (const e of entries) {
    if (e.tutorId !== tutor.id) continue;
    if (e.kind === "session") {
      minutes += e.minutes;
      rateMinutes += lessonRate(e, tutor) * e.minutes;
      sessions++;
    } else if (e.kind === "payout") paidOut += payoutCounts(e) ? e.cents : 0;
    else if (e.kind === "item" && !e.studentId) bought += e.cents;
  }
  const earned = Math.round(rateMinutes / 60) || 0;
  return {
    minutes,
    earnedCents: earned,
    paidOutCents: paidOut,
    boughtCents: bought,
    owedCents: earned - paidOut - bought,
    sessions,
  };
}

/** One line of a tutor's pay, oldest first. Lessons are plus; the rest take away from it. */
export interface TutorPayRow {
  kind: "lesson" | "paid" | "bought";
  on: string;
  /** Plus for a lesson's pay; minus for paid out or bought. */
  cents: number;
  /** The Tutoring record it comes from (a session, payout or purchase). */
  id: string;
  minutes?: number;
  studentId?: string | null;
  /** The calendar lesson, for its times. */
  slotId?: string | null;
  logged?: "auto" | "hand";
  method?: TutoringEntry["method"];
  note?: string;
}

/**
 * A tutor's pay line by line, the way a bank statement reads: each lesson's pay, money you paid
 * them and things they bought. Each lesson's pay is its
 * share of the running total (rounded as it goes), so the lessons add up to exactly what they
 * earned and every counted row adds up to exactly what's credited in their overall total.
 */
export function tutorPayRows(
  tutor: Tutor,
  entries: readonly TutoringEntry[],
): { rows: TutorPayRow[]; earnedCents: number; creditCents: number } {
  const mine = entries.filter((e) => e.tutorId === tutor.id);
  const byDay = (a: { on: string; id: string }, b: { on: string; id: string }) =>
    a.on < b.on ? -1 : a.on > b.on ? 1 : 0;
  const rows: TutorPayRow[] = [];
  let rateMinutes = 0;
  let paidSoFar = 0;
  for (const s of mine.filter((e) => e.kind === "session").sort(byDay)) {
    rateMinutes += lessonRate(s, tutor) * s.minutes;
    const upTo = Math.round(rateMinutes / 60) || 0;
    rows.push({
      kind: "lesson",
      on: s.on,
      cents: upTo - paidSoFar,
      id: s.id,
      minutes: s.minutes,
      studentId: s.studentId,
      slotId: s.slotId ?? null,
      ...(s.logged ? { logged: s.logged } : {}),
    });
    paidSoFar = upTo;
  }
  for (const p of mine.filter((e) => e.kind === "payout")) {
    // Only real payouts: not one removed as a mistake, nor the old coupling's "auto" ones.
    if (!payoutCounts(p)) continue;
    rows.push({
      kind: "paid",
      on: p.on,
      cents: -p.cents,
      id: p.id,
      method: p.method,
      note: p.note,
      ...(p.logged ? { logged: p.logged } : {}),
    });
  }
  for (const b of mine.filter((e) => e.kind === "item" && !e.studentId))
    rows.push({ kind: "bought", on: b.on, cents: -b.cents, id: b.id, note: b.note });
  // Oldest first; a lesson before what was paid against it on the same day.
  const order = { lesson: 0, bought: 1, paid: 2 } as const;
  rows.sort((a, b) => (a.on < b.on ? -1 : a.on > b.on ? 1 : order[a.kind] - order[b.kind]));
  return {
    rows,
    earnedCents: paidSoFar,
    creditCents: rows.reduce((t, r) => t + r.cents, 0),
  };
}

export function hoursText(minutes: number): string {
  const h = minutes / 60;
  return `${Number.isInteger(h) ? h : h.toFixed(1)} ${Math.abs(h) === 1 ? "hour" : "hours"}`;
}

export interface BalanceLine {
  label: string;
  /** Positive = they owe you (the treasurer); negative = you owe them. In cents. */
  cents: number;
  kind: "group" | "tutoring";
}

/**
 * Everything between the treasurer and one person, in one direction: their balance in each
 * group, what their family owes for tutoring, and their tutoring pay (credited against it).
 * Positive net = they owe the treasurer. The net is the exact sum of the lines (overallTotal).
 */
export function overallBalance(
  groups: readonly { name: string; balanceMicro: number }[],
  students: readonly TutoringStudent[],
  tutors: readonly Tutor[],
  entries: readonly TutoringEntry[],
): { lines: BalanceLine[]; netCents: number } {
  const total = overallTotal([
    ...groups.map((g) => ({
      category: "purchases" as const,
      label: g.name,
      balanceMicro: g.balanceMicro,
    })),
    ...students.map((s) => ({
      category: "tutoring" as const,
      label: `Tutoring for ${s.name}`,
      balanceMicro: studentSummary(s.id, entries).balanceCents * 10_000,
    })),
    ...tutors.map((t) => ({
      category: "tutoring" as const,
      label: "Your teaching",
      balanceMicro: -tutorSummary(t, entries).owedCents * 10_000,
    })),
  ]);
  return {
    lines: total.lines.map((l) => ({
      label: l.label,
      cents: l.cents,
      kind: l.category === "purchases" ? "group" : "tutoring",
    })),
    netCents: total.totalCents,
  };
}

export interface LessonProgress {
  /** Prepaid minutes bought (packages and adjustments). 0 means nothing prepaid. */
  prepaidMinutes: number;
  /** Lessons already logged. */
  doneMinutes: number;
  /** Lessons on the calendar that aren't done yet. */
  pendingMinutes: number;
  /** Prepaid minutes not yet spoken for: prepaid − done − pending (below zero = over). */
  leftMinutes: number;
  doneCount: number;
  pendingCount: number;
  /** What the bar counts in: lessons when every lesson is the same length, else hours. */
  unit: "lesson" | "hour";
  /** Minutes in one unit (the lesson length, or 60). */
  unitMinutes: number;
  /** The bar, in minutes out of what's prepaid (see lessonBar). */
  bar: LessonBar;
}

/**
 * The prepaid bar, read like credit left (Miles, 2026-10-23), left to right: green is what's still
 * free to book, yellow is booked and not done yet (it sits at the end of the green and eats into
 * it as lessons are booked), and grey is done. The three always add up to what's prepaid; over
 * it, there's no green, yellow fills what isn't done, and `overMinutes` says by how much.
 */
export interface LessonBar {
  freeMinutes: number;
  bookedMinutes: number;
  doneMinutes: number;
  overMinutes: number;
}

export function lessonBar(prepaid: number, done: number, pending: number): LessonBar {
  const cap = Math.max(0, prepaid);
  const doneIn = Math.min(Math.max(0, done), cap);
  const bookedIn = Math.min(Math.max(0, pending), cap - doneIn);
  return {
    freeMinutes: cap - doneIn - bookedIn,
    bookedMinutes: bookedIn,
    doneMinutes: doneIn,
    overMinutes: Math.max(0, Math.max(0, done) + Math.max(0, pending) - cap),
  };
}

/**
 * A student's prepaid lessons, like credit left: done lessons and booked ones not done yet are
 * "used" out of everything prepaid, and the rest is left (the bar: see lessonBar).
 * Counts in lessons when the student has two or more lessons, all the same length, and the
 * prepaid amount is a whole number of them (10 one-hour lessons), otherwise in hours.
 */
export function lessonProgress(
  studentId: string,
  entries: readonly TutoringEntry[],
  slots: readonly TutoringSlot[],
): LessonProgress {
  const sum = studentSummary(studentId, entries);
  const sessions = entries.filter((e) => e.studentId === studentId && e.kind === "session");
  const pending = slots.filter((s) => s.studentId === studentId && s.status === "booked");
  const lengthOf = (s: TutoringSlot) =>
    Math.round((Date.parse(`${s.endsAt}:00Z`) - Date.parse(`${s.startsAt}:00Z`)) / 60_000);
  // Lesson length from everything they've had or have booked (a cancelled lesson still says how
  // long their lessons are; a done one is already counted by its session).
  const others = slots.filter(
    (s) => s.studentId === studentId && (s.status === "booked" || s.status === "cancelled"),
  );
  const lengths = [...sessions.map((e) => e.minutes), ...others.map(lengthOf)].filter((m) => m > 0);
  const pendingMinutes = pending.reduce((t, s) => t + Math.max(0, lengthOf(s)), 0);
  // Lessons only once there are at least two to go by, all the same length.
  const same = lengths.length > 1 && lengths.every((m) => m === lengths[0]);
  const size = same ? lengths[0]! : 60;
  const whole = sum.boughtMinutes % size === 0;
  const lessons = same && whole;
  return {
    prepaidMinutes: sum.boughtMinutes,
    doneMinutes: sum.usedMinutes,
    pendingMinutes,
    leftMinutes: sum.boughtMinutes - sum.usedMinutes - pendingMinutes,
    doneCount: sessions.length,
    pendingCount: pending.length,
    unit: lessons ? "lesson" : "hour",
    unitMinutes: lessons ? size : 60,
    bar: lessonBar(sum.boughtMinutes, sum.usedMinutes, pendingMinutes),
  };
}

/** Minutes in a progress bar's unit, as a plain number ("6", "1.5"). */
export function inUnits(minutes: number, unitMinutes: number): string {
  const n = minutes / unitMinutes;
  return Number.isInteger(n) ? String(n) : n.toFixed(1).replace(/\.0$/, "");
}
